import React from 'react';
import { Sparkles, BookOpen, CheckCircle2, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePlanningStore } from '@/stores/planningStore';

interface ChecklistPanelCollapsedProps {
  isVisible: boolean;
  onExpand: () => void;
  completedCount: number;
  totalCount: number;
  isAllCompleted?: boolean;
  showAsGuide?: boolean; // Forcer l'affichage en mode Guide (utilisateur a masqué la checklist)
}

/**
 * Onglet plié de la Checklist "Premiers pas" / "Guide" (90px × ~320px)
 * Positionné en dessous du panneau Planning (top: 80%)
 * Change d'apparence une fois la checklist complétée OU si l'utilisateur a masqué la checklist
 * Se masque quand le panneau Planning est ouvert
 */
const ChecklistPanelCollapsed = ({
  isVisible,
  onExpand,
  completedCount,
  totalCount,
  isAllCompleted = false,
  showAsGuide = false,
}: ChecklistPanelCollapsedProps) => {
  // Masquer quand le Planning est ouvert pour éviter la superposition
  const isPlanningOpen = usePlanningStore((state) => state.isOpen);

  if (!isVisible || isPlanningOpen) return null;

  // Couleur du badge selon la progression
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const badgeColor =
    progressPercent === 100
      ? 'bg-green-500'
      : progressPercent >= 50
      ? 'bg-primary'
      : 'bg-orange-500';

  // Mode Guide (complété OU utilisateur a masqué la checklist) vs Mode Checklist (en cours)
  const isGuideMode = isAllCompleted || showAsGuide;

  return (
    <button
      data-miro-target="dashboard-guide-panel"
      onClick={onExpand}
      className={cn(
        // Position fixe bord droit, en dessous du Planning (top: 80%)
        'fixed right-0 top-[80%] -translate-y-1/2 z-50',
        // Dimensions - plus compact en mode guide
        isGuideMode ? 'w-[90px] min-h-[200px]' : 'w-[90px] min-h-[320px]',
        // Style visuel
        'bg-card/95 backdrop-blur-md',
        'border border-r-0 border-border/50 border-b-2 border-b-blue-500/40 border-t-2 border-t-amber-500/50 rounded-l-2xl',
        'shadow-2xl shadow-black/30',
        // Contenu centré en colonne
        'flex flex-col items-center py-5 gap-3',
        // Hover effect
        'hover:w-[100px] hover:bg-card transition-all duration-200',
        'group cursor-pointer'
      )}
    >
      {/* ═══ ICÔNE ═══ */}
      <div className="relative">
        <div className={cn(
          "p-3 rounded-xl group-hover:bg-primary/20 transition-colors",
          isGuideMode ? "bg-blue-500/10" : "bg-primary/10"
        )}>
          {isGuideMode ? (
            <BookOpen className="w-7 h-7 text-blue-500 group-hover:scale-110 transition-transform" />
          ) : (
            <Sparkles className="w-7 h-7 text-primary group-hover:scale-110 transition-transform" />
          )}
        </div>
        {/* Badge - checkmark vert si complété, sinon compteur */}
        {isGuideMode ? (
          <span className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center shadow-lg">
            <CheckCircle2 className="w-4 h-4" />
          </span>
        ) : (
          <span
            className={cn(
              'absolute -top-2 -right-2 w-6 h-6 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg',
              badgeColor
            )}
          >
            {completedCount}
          </span>
        )}
      </div>

      {/* ═══ TEXTE VERTICAL ═══ */}
      {isGuideMode ? (
        <div
          className="text-xs font-bold tracking-widest text-blue-500 group-hover:text-blue-400 transition-colors"
          style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
        >
          GUIDE
        </div>
      ) : (
        <>
          <div
            className="text-xs font-bold tracking-widest text-muted-foreground group-hover:text-foreground transition-colors"
            style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
          >
            PREMIERS
          </div>
          <div
            className="text-xs font-bold tracking-widest text-muted-foreground group-hover:text-foreground transition-colors -mt-1"
            style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
          >
            PAS
          </div>
        </>
      )}

      {/* ═══ CHEVRON AFFORDANCE ═══ */}
      <ChevronLeft className={cn(
        "w-4 h-4 transition-colors",
        isGuideMode ? "text-blue-500/50 group-hover:text-blue-400/80" : "text-muted-foreground/50 group-hover:text-foreground/80"
      )} />

      {/* ═══ SÉPARATEUR ═══ */}
      <div className="w-10 h-px bg-border/50" />

      {/* ═══ COMPTEUR ou TEXTE GUIDE ═══ */}
      {isGuideMode ? (
        <div className="flex flex-col items-center">
          <span className="text-[10px] font-medium text-muted-foreground text-center">
            Cliquez pour
          </span>
          <span className="text-[10px] font-medium text-muted-foreground text-center">
            voir le guide
          </span>
        </div>
      ) : (
        <>
          <div className="flex flex-col items-center">
            <span className="text-2xl font-black text-foreground leading-none">
              {completedCount}/{totalCount}
            </span>
            <span className="text-[10px] font-medium text-muted-foreground/70 mt-1">
              complétés
            </span>
          </div>

          {/* ═══ BARRE DE PROGRESSION MINI ═══ */}
          <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className={cn('h-full rounded-full transition-all duration-500', badgeColor)}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </>
      )}
    </button>
  );
};

export default ChecklistPanelCollapsed;
